<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">AI Model Management</div>
        <h3 class="duallo-table-title">Projects</h3>
      </div>
      <DisplayOverlayWindowButton v-if="canCreate" ref="newProjectWindow" buttonText="Add Project"
        iconName="mdi-plus" windowTitle="Add Project" buttonColor="#e7ff6e">
        <div class="duallo-form add-form">
          <header class="duallo-form-header">
            <div class="duallo-eyebrow">Connect</div>
            <h2 class="duallo-form-title">Roboflow Project</h2>
            <p class="duallo-form-subtitle">Add a Roboflow project (dataset + model) into Duallo. After adding, open it and pick a workflow to run — the project stays off until then.</p>
          </header>

          <div v-if="availableError" class="duallo-inline-error">
            <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
            <span>Couldn't reach Roboflow to list projects. Check the Roboflow configuration and try again.</span>
          </div>
          <div v-if="submitError" class="duallo-inline-error">
            <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
            <span>{{ submitError }}</span>
          </div>

          <v-form ref="form" @submit.prevent="handleSubmit">
            <div class="duallo-form-fields">
              <v-select v-model="selectedProject" :items="registrableProjects" item-title="name" return-object
                label="Roboflow Project" variant="outlined" density="comfortable" hide-details="auto" class="duallo-field"
                :loading="isLoadingAvailable" no-data-text="No unregistered projects available"
                :rules="[v => !!v || 'Select a project']" @update:modelValue="onProjectSelected" />
              <div v-if="selectedProject" class="pick-meta">
                {{ selectedProject.id }}<span v-if="selectedProject.type"> · {{ selectedProject.type }}</span>
                <span v-if="selectedProject.versions != null"> · {{ selectedProject.versions }} version(s)</span>
              </div>
              <v-text-field v-model="displayName" label="Display Name" variant="outlined" density="comfortable"
                hide-details="auto" class="duallo-field" :rules="[v => !!v || 'Display name is required']" />
              <v-textarea v-model="description" label="Description" variant="outlined" density="comfortable"
                hide-details="auto" rows="2" auto-grow class="duallo-field" />
              <v-alert variant="tonal" density="compact" class="soft-alert mt-1">
                The project is added switched off. Open it from the list to select a workflow, then switch it on to run inference.
              </v-alert>
            </div>
            <div class="duallo-form-actions form-actions">
              <button type="submit" class="duallo-btn duallo-btn--admin" :disabled="isSubmitting" @click="handleSubmit">
                <v-progress-circular v-if="isSubmitting" indeterminate size="16" width="2" color="#fff" />
                <span v-else>Add Project</span>
              </button>
            </div>
          </v-form>
        </div>
      </DisplayOverlayWindowButton>
    </header>

    <div class="duallo-table-card">
      <div v-if="toggleError" class="duallo-inline-error">
        <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
        <span>{{ toggleError }}</span>
      </div>

      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="projects.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-cube-scan</v-icon>
        <p class="empty-title">No projects added</p>
        <p class="empty-subtitle">Add a Roboflow project to get started.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-name">Project</th>
                <th class="col-type">Type</th>
                <th class="col-status">Active</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects" :key="project.id">
                <td class="cell-name">
                  <div class="cell-name-main">{{ project.displayName || project.projectName }}</div>
                  <div class="cell-name-sub">{{ project.roboflowProjectId }}</div>
                </td>
                <td><span class="muted">{{ project.projectType || '—' }}</span></td>
                <td>
                  <div class="status-cell">
                    <v-switch v-if="project.baseWorkflowUrl" :model-value="project.isActive" color="#64ffda"
                      density="compact" hide-details inset :disabled="!canUpdate" :loading="togglingId === project.id"
                      @update:modelValue="toggleActive(project)" />
                    <span v-else class="needs-workflow">Needs workflow</span>
                  </div>
                </td>
                <td class="cell-actions">
                  <button type="button" class="duallo-btn duallo-btn--ghost" @click="openDetail(project)">
                    <v-icon size="16">mdi-tune-variant</v-icon><span>Manage</span>
                  </button>
                  <button v-if="canDelete" type="button" class="duallo-btn duallo-btn--ghost is-danger" @click="confirmDelete(project)">
                    <v-icon size="16">mdi-trash-can-outline</v-icon><span>Remove</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <article v-for="project in projects" :key="project.id" class="duallo-list-card">
            <div class="card-top">
              <div>
                <div class="card-name">{{ project.displayName || project.projectName }}</div>
                <div class="cell-name-sub">{{ project.roboflowProjectId }}</div>
              </div>
              <div class="status-cell">
                <v-switch v-if="project.baseWorkflowUrl" :model-value="project.isActive" color="#64ffda"
                  density="compact" hide-details inset :disabled="!canUpdate" :loading="togglingId === project.id"
                  @update:modelValue="toggleActive(project)" />
                <span v-else class="needs-workflow">Needs workflow</span>
              </div>
            </div>
            <div class="card-actions">
              <button type="button" class="duallo-btn duallo-btn--ghost" @click="openDetail(project)">
                <v-icon size="16">mdi-tune-variant</v-icon><span>Manage</span>
              </button>
              <button v-if="canDelete" type="button" class="duallo-btn duallo-btn--ghost is-danger" @click="confirmDelete(project)">
                <v-icon size="16">mdi-trash-can-outline</v-icon><span>Remove</span>
              </button>
            </div>
          </article>
        </div>
      </template>
    </div>

    <ProjectDetailDialog v-model="detailOpen" :project="detailProject" :readonly="!canUpdate"
      @updated="onProjectUpdated" />

    <v-dialog v-model="deleteOpen" max-width="420" persistent>
      <div class="duallo-modal">
        <header class="duallo-modal-header">
          <div class="duallo-eyebrow">Remove</div>
          <h2 class="duallo-form-title">{{ pendingDelete?.displayName || pendingDelete?.projectName }}</h2>
          <p class="duallo-form-subtitle">This removes the project from Duallo. This cannot be undone.</p>
        </header>
        <div v-if="deleteError" class="duallo-inline-error">
          <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
          <span>{{ deleteError }}</span>
        </div>
        <div class="duallo-modal-actions">
          <button type="button" class="duallo-btn duallo-btn--text" :disabled="isDeleting" @click="deleteOpen = false">Cancel</button>
          <button type="button" class="duallo-btn duallo-btn--danger" :disabled="isDeleting" @click="handleDelete">
            <v-progress-circular v-if="isDeleting" indeterminate size="16" width="2" color="#fff" />
            <span v-else>Remove</span>
          </button>
        </div>
      </div>
    </v-dialog>
  </section>
</template>

<script setup>
import { defineOptions, onMounted, ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import DisplayOverlayWindowButton from '../buttons/DisplayOverlayWindowButton.vue';
import ProjectDetailDialog from '@/components/ProjectDetailDialog.vue';
import AiProjectService from '@/services/AiProjectService';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

defineOptions({ name: 'AiProjectManagementTable' });

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const { can } = usePermissions();
const canCreate = computed(() => can(Permissions.AIWorkflowCreate));
const canUpdate = computed(() => can(Permissions.AIWorkflowUpdate));
const canDelete = computed(() => can(Permissions.AIWorkflowDelete));

const projects = ref([]);
const isLoading = ref(true);
const togglingId = ref(null);
const toggleError = ref('');

const newProjectWindow = ref(null);
const form = ref(null);
const availableProjects = ref([]);
const isLoadingAvailable = ref(false);
const availableError = ref(false);
const selectedProject = ref(null);
const selectedDetail = ref(null);
const displayName = ref('');
const description = ref('');
const isSubmitting = ref(false);
const submitError = ref('');

const detailOpen = ref(false);
const detailProject = ref(null);

const deleteOpen = ref(false);
const pendingDelete = ref(null);
const isDeleting = ref(false);
const deleteError = ref('');

const registrableProjects = computed(() => {
  const registered = new Set(projects.value.map(p => p.roboflowProjectId));
  return availableProjects.value.filter(p => !registered.has(p.id));
});

onMounted(async () => {
  try {
    projects.value = await AiProjectService.getProjects();
  } finally {
    isLoading.value = false;
  }
  loadAvailable();
});

const loadAvailable = async () => {
  isLoadingAvailable.value = true;
  availableError.value = false;
  try {
    availableProjects.value = await AiProjectService.getAvailableRoboflowProjects();
  } catch {
    availableError.value = true;
  } finally {
    isLoadingAvailable.value = false;
  }
};

const onProjectSelected = async (project) => {
  selectedDetail.value = null;
  if (project && !displayName.value) displayName.value = project.name ?? '';
  if (!project) return;
  const slug = (project.id || '').split('/').pop();
  try {
    selectedDetail.value = await AiProjectService.getRoboflowProjectDetail(project.workspace, slug);
  } catch {
    selectedDetail.value = null;
  }
};

const resetForm = () => {
  selectedProject.value = null;
  selectedDetail.value = null;
  displayName.value = '';
  description.value = '';
  submitError.value = '';
  form.value?.reset();
};

const handleSubmit = async () => {
  submitError.value = '';
  const validation = await form.value?.validate?.();
  if (validation && validation.valid === false) return;
  if (!selectedProject.value) return;

  isSubmitting.value = true;
  try {
    const p = selectedProject.value;
    const detail = selectedDetail.value;
    // Capture classes (the picker already has them) + versions from the detail when available, so
    // the detail window shows them without a live Roboflow call. No workflow is set at add time —
    // the admin selects a real workflow from the detail window, which is what inference runs.
    const snapshot = {
      classes: detail?.classes ?? p.classes ?? [],
      versions: detail?.versions ?? [],
    };
    await AiProjectService.createProject({
      roboflowWorkspace: p.workspace,
      roboflowProjectId: p.id,
      projectName: p.name,
      projectType: p.type,
      displayName: displayName.value,
      description: description.value,
      metadataJson: JSON.stringify(snapshot),
    });
    projects.value = await AiProjectService.getProjects();
    resetForm();
    newProjectWindow.value?.closeWindow();
  } catch (e) {
    submitError.value = e?.status === 409
      ? 'This Roboflow project is already added.'
      : (e?.message || 'Could not add the project. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
};

const toggleActive = async (project) => {
  toggleError.value = '';
  togglingId.value = project.id;
  try {
    const updated = await AiProjectService.updateProject(project.id, {
      displayName: project.displayName,
      description: project.description,
      isActive: !project.isActive,
    });
    if (updated) {
      const idx = projects.value.findIndex(p => p.id === project.id);
      if (idx !== -1) projects.value[idx] = updated;
    }
  } catch (e) {
    const name = project.displayName || project.projectName || 'project';
    toggleError.value = e?.message || `Could not update "${name}". Please try again.`;
  } finally {
    togglingId.value = null;
  }
};

const openDetail = (project) => {
  detailProject.value = project;
  detailOpen.value = true;
};

const onProjectUpdated = (updated) => {
  if (!updated) return;
  const idx = projects.value.findIndex(p => p.id === updated.id);
  if (idx !== -1) projects.value[idx] = updated;
  detailProject.value = updated;
};

const confirmDelete = (project) => {
  pendingDelete.value = project;
  deleteError.value = '';
  deleteOpen.value = true;
};

const handleDelete = async () => {
  if (!pendingDelete.value) return;
  deleteError.value = '';
  isDeleting.value = true;
  try {
    await AiProjectService.deleteProject(pendingDelete.value.id);
    projects.value = projects.value.filter(p => p.id !== pendingDelete.value.id);
    deleteOpen.value = false;
    pendingDelete.value = null;
  } catch (e) {
    deleteError.value = e?.message || 'Could not remove the project. Please try again.';
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
/* Table-specific bits only — shared shell/form/button/modal styling lives in duallo-components.css. */
.col-name { min-width: 240px; }
.col-type { width: 160px; }
.col-status { width: 90px; }
.col-actions { width: 190px; }
.duallo-data-table td { padding: 10px 16px; }   /* slightly denser rows for this table */

.cell-name-main { color: #fff; font-weight: 600; }
.cell-name-sub { font-size: 0.78rem; color: #8aa0aa; font-family: 'JetBrains Mono', Consolas, monospace; }
.muted { color: #8aa0aa; }
.cell-actions { display: flex; gap: 8px; align-items: center; }
.status-cell { display: flex; align-items: center; gap: 8px; }
.needs-workflow {
  font-size: 0.68rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  color: #ffcc80; background: rgba(255, 204, 128, 0.1); border: 1px solid rgba(255, 204, 128, 0.28);
  padding: 2px 8px; border-radius: 999px; white-space: nowrap;
}

.card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.card-name { font-size: 1rem; font-weight: 700; color: #fff; }
.card-actions { display: flex; gap: 8px; }

.add-form { min-width: min(460px, 80vw); user-select: none; }
.pick-meta { font-size: 0.78rem; color: #8aa0aa; font-family: 'JetBrains Mono', Consolas, monospace; margin-top: -4px; }
.soft-alert { background: rgba(255, 204, 128, 0.08); border: 1px solid rgba(255, 204, 128, 0.28); color: #ffcc80; font-size: 0.82rem; }
.form-actions { margin-top: 18px; }

.empty-state { align-items: center; text-align: center; padding: 40px 16px; color: #8aa0aa; }
.empty-title { font-size: 1.05rem; font-weight: 600; color: #cfd8dc; margin: 12px 0 4px; }
.empty-subtitle { font-size: 0.88rem; margin: 0; }
</style>
